import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, BookOpen, Download, Award, Zap, Lock, Clock, Gift } from "lucide-react";
import { useIsNativeApp } from "@/hooks/useIsNativeApp";
import { useCountdown } from "@/hooks/useCountdown";
import { supabase } from "@/integrations/supabase/client";

interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lessonTitle?: string;
  /**
   * Warum das Fenster aufgeht: eine gesperrte Lektion (Standard) oder das
   * aufgebrauchte Miko-Kontingent (Chats und Werkzeuge). Bis 07.10.2026 sagte
   * das Fenster auch am Miko-Limit „Diese Lektion ist Teil des vollständigen Kurses.“
   */
  anlass?: "lektion" | "miko";
}

/** Satz am Miko-Limit: Start-Konten haben ihre Anfragen insgesamt, nicht je Monat. */
export const MIKO_LIMIT_TEXT = "Deine Miko-Anfragen zum Ausprobieren sind aufgebraucht. Mit der Mitgliedschaft fragst du Miko unbegrenzt.";

export function UpgradeDialog({ open, onOpenChange, lessonTitle, anlass = "lektion" }: UpgradeDialogProps) {
  const miko = anlass === "miko";
  const isNativeApp = useIsNativeApp();
  const [offerData, setOfferData] = useState<{ inWindow: boolean; expiresAt: string | null }>({ inWindow: false, expiresAt: null });
  const [loading, setLoading] = useState(false);
  const countdown = useCountdown(offerData.expiresAt);

  // Check offer window when dialog opens
  useEffect(() => {
    if (!open || isNativeApp) return;
    
    const checkOffer = async () => {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;
        const user = session.user;
        
        const { data, error } = await supabase.rpc('is_in_welcome_offer_window', { uid: user.id });
        if (!error && data) {
          const result = data as unknown as { in_window: boolean; offer_expires_at?: string };
          setOfferData({
            inWindow: result.in_window,
            expiresAt: result.offer_expires_at || null,
          });
        }
      } catch (err) {
        console.error("Offer check failed:", err);
      } finally {
        setLoading(false);
      }
    };
    
    checkOffer();
  }, [open, isNativeApp]);

  const handleUpgrade = () => {
    onOpenChange(false);
    if (offerData.inWindow) {
      window.location.href = "https://www.musikonzept.de/abo-checkout/pro?interval=monthly&coupon=MUSIK10";
    } else {
      window.location.href = "https://www.musikonzept.de/abo-checkout/pro?interval=monthly";
    }
  };

  // Native App: Informative Variante ohne Kaufbutton (Apple Reader App Compliance)
  if (isNativeApp) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
              <Lock className="h-6 w-6 text-muted-foreground" />
            </div>
            <DialogTitle className="text-xl">Nur für Mitglieder</DialogTitle>
            <DialogDescription className="text-center">
              {miko
                ? MIKO_LIMIT_TEXT
                : lessonTitle 
                ? `„${lessonTitle}" ist nur für Mitglieder verfügbar.`
                : "Dieser Inhalt ist nur für Mitglieder der MusiKonzept-Plattform verfügbar."
              }
            </DialogDescription>
          </DialogHeader>

          {/* In der App kein Hinweis auf den Kauf woanders (Sitzung A1, Frage 395). */}

          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="w-full"
          >
            Verstanden
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  // Special offer variant (Day 7-9)
  if (offerData.inWindow && !countdown.isExpired) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-accent/20 to-primary/10 flex items-center justify-center mb-4">
              <Gift className="h-6 w-6 text-accent" />
            </div>
            <Badge className="mx-auto mb-3 bg-destructive/10 text-destructive hover:bg-destructive/20">
              <Clock className="h-3 w-3 mr-1" />
              Nur noch {countdown.formatted}!
            </Badge>
            <DialogTitle className="text-xl">Dein exklusives Angebot</DialogTitle>
            <DialogDescription className="text-center">
              {miko
                ? MIKO_LIMIT_TEXT
                : lessonTitle 
                ? `Schalte „${lessonTitle}" und alle 2.400+ Videos frei!`
                : "Schalte jetzt alle 2.400+ Videos für nur 1 € frei!"
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Price display */}
            <div className="text-center p-4 rounded-xl bg-gradient-to-br from-primary/5 via-accent/5 to-primary/10 border border-primary/20">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="text-muted-foreground line-through text-lg">39 €/Monat</span>
                <span className="text-2xl font-bold text-foreground">29 €/Monat</span>
              </div>
              <p className="text-sm text-primary font-medium">+ 14 Tage Testzugang für nur 1 €</p>
              <p className="text-xs text-muted-foreground mt-1">10 € Dauer-Rabatt auf jede Rechnung</p>
            </div>

            <div className="grid gap-3">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <BookOpen className="h-5 w-5 text-primary shrink-0" />
                <span className="text-sm">Alle Kurse & Lektionen</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <Download className="h-5 w-5 text-primary shrink-0" />
                <span className="text-sm">Downloads & Materialien</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <Award className="h-5 w-5 text-primary shrink-0" />
                <span className="text-sm">Zertifikate</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <Zap className="h-5 w-5 text-primary shrink-0" />
                <span className="text-sm">Unbegrenzte Miko-Anfragen</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button 
              onClick={handleUpgrade} 
              className="w-full bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
            >
              <Gift className="h-4 w-4 mr-2" />
              Jetzt 1 € Deal & Rabatt sichern
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => onOpenChange(false)}
              className="w-full text-muted-foreground"
            >
              Später
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Default variant (outside offer window)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mb-4">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <Badge className="mx-auto mb-3 bg-accent/10 text-accent hover:bg-accent/20">
            14 Tage für 1 € testen
          </Badge>
          <DialogTitle className="text-xl">{miko ? "Möchtest du weiter mit Miko planen?" : "Möchtest du mehr sehen?"}</DialogTitle>
          <DialogDescription className="text-center">
            {miko
              ? MIKO_LIMIT_TEXT
              : lessonTitle 
              ? `„${lessonTitle}" ist Teil des vollständigen Kurses.`
              : "Diese Lektion ist Teil des vollständigen Kurses."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground text-center">
            Teste die Mitgliedschaft 14 Tage für nur 1 € und erhalte Zugang zu:
          </p>

          <div className="grid gap-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <BookOpen className="h-5 w-5 text-primary shrink-0" />
              <span className="text-sm">Alle Kurse & Lektionen</span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Download className="h-5 w-5 text-primary shrink-0" />
              <span className="text-sm">Downloads & Materialien</span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Award className="h-5 w-5 text-primary shrink-0" />
              <span className="text-sm">Zertifikate</span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Zap className="h-5 w-5 text-primary shrink-0" />
              <span className="text-sm">Unbegrenzte Miko-Anfragen</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Button 
            onClick={handleUpgrade} 
            className="w-full bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            14 Tage für 1 € testen
          </Button>
          <Button 
            variant="ghost" 
            onClick={() => onOpenChange(false)}
            className="w-full text-muted-foreground"
          >
            Später
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
